import React from 'react';
import { analytics } from 'lib/analytics';
import { jwtDecode } from 'jwt-decode';
import { styled } from '@mui/material/styles';
import {
  LogoImage,
  PageTitle,
  PageSubtitle,
} from '../../../../features/freeTrial/Styles';
import { useFreeTrialContext } from 'providers/FreeTrialProvider';
import { useFreeTrialLogin } from 'hooks/useLoginUser';
import Box from '@mui/material/Box';
import Button from 'components/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import config from 'data/config';

export default function SiteReady() {
  const { state } = useFreeTrialContext();
  const { routeLogin, isLoading: isLoggingInUser } = useFreeTrialLogin();

  return (
    <Content>
      <Stack direction="row" alignItems="center" gap={2}>
        <LogoImage />
        <PageTitle>WorkforceHub is Ready</PageTitle>
      </Stack>
      <Divider sx={{ mt: 2, mb: 4 }} />
      <Stack gap={1} sx={{ mb: 4 }}>
        <PageSubtitle>
          <span className="attention">Welcome to WorkforceHub!</span>
        </PageSubtitle>
        <PageSubtitle>
          As you navigate through the platform, feel free to take advantage of
          our resources.
        </PageSubtitle>

        <WebList>
          <WebListLink
            href="https://www.workforcehub.com/workforcehub-university/"
            label="WorkforceHub University"
            color="primary"
          >
            WorkforceHub University
          </WebListLink>

          <WebListLink
            href="https://support.swipeclock.com/workforcehub"
            label="WorkforceHub knowledge base"
            color="primary"
          >
            WorkforceHub knowledge base
          </WebListLink>
        </WebList>
        <Divider sx={{ mt: 2, mb: 2 }} />
        <PageSubtitle>
          Make it easy to get back here - bookmark the login page now.{' '}
          <WebLink href={`${config.wfhUiUrl}/login`}>
            {`${config.wfhUiUrl}/login`}
          </WebLink>
        </PageSubtitle>

        <PageSubtitle>Your business email will be your login.</PageSubtitle>
      </Stack>
      <Stack alignItems="center">
        <Button
          loading={isLoggingInUser}
          onClick={() => {
            const { site } = jwtDecode(state?.userToken);
            analytics.freeTrial.trackWFHClicked(site);
            routeLogin(state?.userToken);
          }}
        >
          Take Me to WorkforceHub
        </Button>
      </Stack>
    </Content>
  );
}

const Content = styled(Box)(({ theme }) => ({
  margin: `${theme.spacing(4)} auto`,
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(0),
  },
  [theme.breakpoints.down('md')]: {
    width: 'auto',
  },
}));

const WebLink = styled((props) => (
  <Link
    underline="hover"
    target="_blank"
    rel="noopener noreferrer"
    {...props}
  />
))(({ theme }) => ({
  color: theme.palette.primary.main,
  '&:hover': {
    color: theme.palette.primary.light,
  },
}));

const WebListLink = styled((props) => (
  <li>
    <WebLink {...props} />
  </li>
))(({ theme }) => ({
  color: theme.palette.primary.main,
  '&:hover': {
    color: theme.palette.primary.light,
  },
}));

const WebList = styled((props) => (
  <Stack gap={1} component="ul" direction="column" {...props} />
))(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
}));
